import favoriteFilled from "@iconify/icons-mdi/heart";
import favoriteBorder from "@iconify/icons-mdi/heart-outline";
import { Icon } from "@iconify/react";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { likeProduct, unlikeProduct, isProductLiked } from "../../store/reducers/userReducer";
import { useLikedProducts } from "../contexts/LikedProductsContext";

const ProductCard = memo(
  ({
    image,
    title,
    onClick,
    maxHeight,
    upc,
    height,
    description,
    classification,
  }) => {
    const { addLikedProduct, removeLikedProduct } =
      useLikedProducts();
    const [bgColor, setBgColor] = useState("white");
    const [processedImage, setProcessedImage] = useState(image);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const snackbar = useSnackbar();
    const [style, setStyle] = useState({
      opacity: 0,
      visibility: "hidden",
      transition: "opacity 0.5s ease, visibility 0.5s ease",
    });
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    console.log('user:', user);
    const cardRef = useRef(null);
    const isLiked = useSelector((state) => isProductLiked(state.user.user, upc));
    const product = { image, title, upc, description, classification };

    const navigate = useNavigate();

    const handleLikeClicked = (e) => {
      e.stopPropagation();
        // Check if user is logged in
      if (!user || !user.token) {
        snackbar.enqueueSnackbar("Please log in to like products!", {
          variant: "error",
          autoHideDuration: 2000,
        });
        return;
      }

      if (isLiked) {
        dispatch(unlikeProduct({product: product}));
        
      } else {
        dispatch(likeProduct({product: product}));
      }
    };

    // Debounce resize event handler
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    };

    const handleResize = useCallback(
      debounce(() => {
        setIsMobile(window.innerWidth <= 768);
      }, 200),
      []
    );

    useEffect(() => {
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [handleResize]);

    // Function to remove white/near-white background from the image
    const removeWhiteBackground = (img) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);

      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const alpha = data[i + 3];

        // If the pixel is nearly white, make it transparent
        if (r > 240 && g > 240 && b > 240 && alpha > 0) {
          data[i + 3] = 0; // Set alpha to 0 to make it transparentd
        }
      }

      ctx.putImageData(imageData, 0, 0);

      return canvas.toDataURL("image/png");
    };

    // Extract dominant color and remove white background from the image
    // useEffect(() => {
    //   const img = new Image();
    //   img.src = image ? image : `https://placehold.co/400x400?text=${title}`;
    //   img.crossOrigin = "Anonymous";
    //   img.onload = () => {
    //     const colorThief = new ColorThief();
    //     const dominantColor = colorThief.getColor(img);
    //     const rgbaColor = `rgba(${dominantColor[0]}, ${dominantColor[1]}, ${dominantColor[2]}, 0.3)`;
    //     setBgColor(rgbaColor);

    //     const processedImageUrl = removeWhiteBackground(img);
    //     setProcessedImage(processedImageUrl);
    //   };
    // }, [image, title]);

    // Ensure visibility for mobile
    useEffect(() => {
      if (isMobile) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setStyle({
                  opacity: 1,
                  visibility: "visible",
                  transition: "opacity 0.5s ease, visibility 1s ease",
                });
              } else {
                setStyle({
                  opacity: 0,
                  visibility: "hidden",
                  transition: "opacity 0.5s ease, visibility 1s ease",
                });
              }
            });
          },
          {
            threshold: 0.9,
          }
        );

        if (cardRef.current) {
          observer.observe(cardRef.current);
        }

        return () => {
          if (cardRef.current) {
            observer.unobserve(cardRef.current);
          }
        };
      } else {
        // Ensure the card is hidden by default on desktop
        setStyle({
          opacity: 0,
          visibility: "hidden",
          transition: "opacity 0.5s ease, visibility 0.5s ease",
        });
      }
    }, [isMobile]);

    return (
      <Box
        ref={cardRef}
        sx={{
          cursor: "pointer",
          maxWidth: 180,
          marginBottom: 3,
          width: "100%",
          "@media (max-width: 768px)": {
            height: height ? height : "28vh",
          },
          "@media (min-width: 769px)": {
            height: "28vh",
          },
          "@media (max-height: 750px)": {
            height: "29vh",
          },
          "@media (min-height: 751px)": {
            height: "27vh",
          },
        }}
        onClick={onClick}
        onMouseEnter={() =>
          !isMobile &&
          setStyle({
            opacity: 1,
            visibility: "visible",
            transition: "opacity 0.5s ease, visibility 1s ease",
          })
        }
        onMouseLeave={() =>
          !isMobile &&
          setStyle({
            opacity: 0,
            visibility: "hidden",
            transition: "opacity 0.5s ease, visibility 1s ease",
          })
        }
      >
        <Card
          sx={{
            padding: 0.7,
            borderRadius: 5,
            boxShadow: "10px 12px 20px rgba(0,0,0,0.05)",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Box
            sx={{
              backgroundColor: bgColor,
              borderRadius: "12px",
              padding: "10px",
              marginBottom: "16px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              position: "relative",
              transition: "background-color 0.5s ease",
              height: maxHeight ? maxHeight : 120,
              boxSizing: "border-box",
            }}
          >
            <CardMedia
              component="img"
              image={
                processedImage ||
                image ||
                `https://placehold.co/400x400?text=${title}`
              }
              alt={title}
              sx={{
                width: "auto",
                maxHeight: "100%",
                objectFit: "contain",
              }}
            />
          </Box>

          <CardContent
            sx={{
              width: "100%",
              textAlign: "left",
              padding: 0,
              flex: "1 0 auto",
            }}
          >
            <Typography
              gutterBottom
              variant="body2"
              component="div"
              sx={{
                fontWeight: "600",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                lineHeight: 1.2,
                maxHeight: "2.4em",
              }}
            >
              {title}
            </Typography>
          </CardContent>

          <Box
            style={style}
            sx={{
              pt: 2,
              position: "absolute",
              bottom: 5,
              right: 4,
              display: "flex",
              flexDirection: "row",
              opacity: style.opacity,
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                handleLikeClicked(e);
              }}
            >
              {isLiked ? (
                <Icon
                  icon={favoriteFilled}
                  fontSize="large"
                  style={{ color: "#EF4F26" }}
                />
              ) : (
                <Icon
                  icon={favoriteBorder}
                  fontSize="large"
                  style={{ color: "#99B2C3" }}
                />
              )}
            </IconButton>

            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/products/chat/${upc}`);
              }}
            >
              <Icon
                icon="heroicons:chat-bubble-left-ellipsis"
                fontSize="large"
                style={{ color: "#99B2C3" }}
              />
            </IconButton>

            {/* <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/products/details/${upc}`);
              }}
            >
              <Icon
                icon={"bx:store"}
                fontSize="large"
                style={{ color: "#99B2C3" }}
              />
            </IconButton> */}
          </Box>
        </Card>
      </Box>
    );
  }
);

export default ProductCard;
